import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  TrendingUp, 
  TrendingDown,
  LineChart, 
  Clock, 
  ArrowLeft, 
  FolderPlus,
  AlertCircle,
  CheckCircle,
  BarChart2,
  RefreshCw,
  Wifi,
  WifiOff
} from 'lucide-react';
import { Navigation } from '@/components/layout/Navigation';
import { Sidebar } from '@/components/layout/Sidebar';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { BloombergRadioButton } from './BloombergRadioButton';
import { vmTradingService } from '@/services/vmTradingService';
import { deploymentService } from '@/services/deploymentService';
import { useToast } from '@/hooks/use-toast';

// Define TypeScript interfaces for our data structures
interface TradeActivity {
  id: string;
  action: 'buy' | 'sell';
  symbol: string;
  quantity: number;
  price: number;
  time: string;
  status: 'executed' | 'pending' | 'cancelled' | 'failed' | 'filled' | 'open' | 'rejected';
  timeAgo: string;
  source: 'live' | 'paper' | 'backtest';
  strategyId?: string;
  strategyName?: string;
}

// VM Trading Integration - No account ID needed as VM handles authentication

interface StrategyActivity {
  id: string;
  name: string;
  type: 'live' | 'paper' | 'backtest';
  lastUpdate: string;
  timeAgo: string;
  performance: {
    value: number;
    percentage: number;
  };
  positions: number;
  tradingMode?: string;
  status?: string;
  symbols?: string[];
}

interface OrderActivity {
  id: string;
  symbol: string;
  action: 'buy' | 'sell';
  type: 'market' | 'limit' | 'stop';
  quantity: number;
  price?: number;
  status: 'open' | 'filled' | 'cancelled' | 'rejected';
  timeAgo: string;
  source: 'live' | 'paper' | 'backtest';
}

export const TradingHistory = () => {
  const [activeView, setActiveView] = useState('chat');
  const [showRoles, setShowRoles] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Use real state variables that would be populated from API or props
  const [tradeActivities, setTradeActivities] = useState<TradeActivity[]>([]);
  const [strategyActivities, setStrategyActivities] = useState<StrategyActivity[]>([]);
  const [orderActivities, setOrderActivities] = useState<OrderActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'unknown'>('unknown');
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // Helper function to calculate time ago with improved date parsing
  const getTimeAgo = (dateString: string): string => {
    try {
      const now = new Date();
      let date: Date;
      
      // Handle various date formats that might come from the VM
      if (dateString.includes('T') || dateString.includes('-')) {
        // ISO format or similar
        date = new Date(dateString);
      } else {
        // Try parsing as timestamp
        const timestamp = parseInt(dateString);
        if (!isNaN(timestamp)) {
          date = new Date(timestamp * 1000); // Convert from seconds if needed
        } else {
          date = new Date(dateString);
        }
      }
      
      // Validate the date
      if (isNaN(date.getTime())) {
        console.warn(`[TradingHistory] Invalid date string: ${dateString}`);
        return 'Unknown';
      }
      
      // Enhanced debugging for date parsing issues
      if (process.env.NODE_ENV === 'development') {
        console.log(`[TradingHistory] Date parsing debug:`, {
          input: dateString,
          parsed: date.toISOString(),
          year: date.getFullYear(),
          currentDate: now.toISOString(),
          diffInSeconds: Math.floor((now.getTime() - date.getTime()) / 1000),
          note: '2025 dates are CORRECT - current year is 2025'
        });
      }
      
      const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
      
      if (diffInSeconds < 0) {
        // Future date - might be a timezone issue
        console.warn(`[TradingHistory] Future date detected: ${dateString} -> ${date.toISOString()}`);
        return 'Just now';
      }
      
      if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
      
      const days = Math.floor(diffInSeconds / 86400);
      if (days === 1) return '1 day ago';
      if (days < 30) return `${days} days ago`;
      
      const months = Math.floor(days / 30);
      if (months === 1) return '1 month ago';
      if (months < 12) return `${months} months ago`;
      
      const years = Math.floor(months / 12);
      return years === 1 ? '1 year ago' : `${years} years ago`;
    } catch (error) {
      console.warn(`[TradingHistory] Error parsing date: ${dateString}`, error);
      return 'Unknown';
    }
  };

    // Enhanced data fetching with better error handling and connection status
  const fetchBrokerData = async (isManualRefresh: boolean = false) => {
    if (isManualRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    
    setErrorMessage(null);
    
    try {
      console.log(`[TradingHistory] ${isManualRefresh ? 'Manual refresh' : 'Fetching data'} from VM trading service...`);

      // Add timeout for localhost development to prevent hanging
      const isLocalhost = typeof window !== 'undefined' && 
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      
      const timeoutMs = isLocalhost ? 5000 : 15000; // Increased timeouts for better reliability
      
      const withTimeout = (promise: Promise<any>) => {
        return Promise.race([
          promise,
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
          )
        ]);
      };

      // Fetch orders, positions, and deployed strategies from BOTH trading modes
      const [paperOrders, liveOrders, paperPositions, livePositions, deployedStrategies] = await Promise.all([
        withTimeout(vmTradingService.getOrders('PAPER')).catch(err => {
          console.warn('[TradingHistory] VM PAPER orders fetch failed:', err.message);
          return [];
        }),
        withTimeout(vmTradingService.getOrders('LIVE')).catch(err => {
          console.warn('[TradingHistory] VM LIVE orders fetch failed:', err.message);
          return [];
        }),
        withTimeout(vmTradingService.getPositions('PAPER')).catch(err => {
          console.warn('[TradingHistory] VM PAPER positions fetch failed:', err.message);
          return [];
        }),
        withTimeout(vmTradingService.getPositions('LIVE')).catch(err => {
          console.warn('[TradingHistory] VM LIVE positions fetch failed:', err.message);
          return [];
        }),
        withTimeout(deploymentService.listDeployedStrategies()).catch(err => {
          console.warn('[TradingHistory] Deployed strategies fetch failed:', err.message);
          return { success: false, deployed_strategies: [] };
        })
      ]);

      // Combine orders from both trading modes
      const vmOrders = [...paperOrders, ...liveOrders];
      const vmPositions = [...paperPositions, ...livePositions];

      // Safe parsing helper - fixed to handle 0 values correctly
      const safeParseFloat = (value: any, defaultValue: number = 0): number => {
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
          const parsed = parseFloat(value);
          return isNaN(parsed) ? defaultValue : parsed;
        }
        return defaultValue;
      };

      // Tag orders with their trading mode before combining
      const taggedPaperOrders = paperOrders.map(order => ({ ...order, _tradingMode: 'PAPER' }));
      const taggedLiveOrders = liveOrders.map(order => ({ ...order, _tradingMode: 'LIVE' }));

      // Map VM orders to TradeActivity with enhanced debugging and better date handling
      const mappedTrades: TradeActivity[] = [...taggedPaperOrders, ...taggedLiveOrders].map((order: any) => {
        const tradingMode = order._tradingMode;
        const associatedPosition = vmPositions.find(p => p.symbol === order.symbol);
        
        // Debug price parsing
        if (process.env.NODE_ENV === 'development') {
          console.log(`[TradingHistory] Processing ${tradingMode} order:`, order);
          console.log(`[TradingHistory] Available price fields:`, {
            filled_avg_price: order.filled_avg_price,
            price: order.price,
            limit_price: order.limit_price,
            position_avg_entry_price: associatedPosition?.avg_entry_price
          });
        }
        
        // Improved price parsing - try multiple fields
        let orderPrice = 0;
        if (order.filled_avg_price !== null && order.filled_avg_price !== undefined) {
          orderPrice = safeParseFloat(order.filled_avg_price);
          if (process.env.NODE_ENV === 'development') {
            console.log(`[TradingHistory] Using filled_avg_price: ${orderPrice}`);
          }
        } else if (associatedPosition?.avg_entry_price) {
          orderPrice = safeParseFloat(associatedPosition.avg_entry_price);
          if (process.env.NODE_ENV === 'development') {
            console.log(`[TradingHistory] Using position avg_entry_price: ${orderPrice}`);
          }
        } else if (order.price !== null && order.price !== undefined) {
          orderPrice = safeParseFloat(order.price);
          if (process.env.NODE_ENV === 'development') {
            console.log(`[TradingHistory] Using price: ${orderPrice}`);
          }
        } else if (order.limit_price !== null && order.limit_price !== undefined) {
          orderPrice = safeParseFloat(order.limit_price);
          if (process.env.NODE_ENV === 'development') {
            console.log(`[TradingHistory] Using limit_price: ${orderPrice}`);
          }
        } else {
          console.warn(`[TradingHistory] No valid price found for order ${order.id}`, order);
        }
        
        // Enhanced timestamp handling for Alpaca orders - prioritize actual execution time
        let executionTime: string;
        let timestampSource = 'fallback';
        
        // For filled orders, prioritize filled_at (actual execution time from Alpaca)
        if (order.status === 'filled' && order.filled_at && order.filled_at !== null) {
          executionTime = order.filled_at;
          timestampSource = 'filled_at';
        }
        // For submitted/pending orders, use submitted_at if available
        else if (['submitted', 'pending_new', 'accepted'].includes(order.status) && order.submitted_at) {
          executionTime = order.submitted_at;
          timestampSource = 'submitted_at';
        }
        // Fallback chain for other cases
        else if (order.updated_at) {
          executionTime = order.updated_at;
          timestampSource = 'updated_at';
        }
        else if (order.created_at) {
          executionTime = order.created_at;
          timestampSource = 'created_at';
        }
        else {
          executionTime = new Date().toISOString();
          timestampSource = 'current_time';
        }
        
          // Enhanced debugging for timestamp issues
        if (process.env.NODE_ENV === 'development') {
          console.log(`[TradingHistory] ${order.symbol} (${order.status}) FULL ORDER DEBUG:`, {
            order_id: order.id,
            symbol: order.symbol,
            status: order.status,
            filled_at: order.filled_at,
            submitted_at: order.submitted_at,
            updated_at: order.updated_at,
            created_at: order.created_at,
            chosen_timestamp: executionTime,
            timestamp_source: timestampSource,
            full_order_object: order, // Log the entire order to see what fields are available
            parsed_execution_time: new Date(executionTime).toISOString(),
            is_valid_date: !isNaN(new Date(executionTime).getTime())
          });
        }
        
        const trade = {
        id: order.id || `vm_trade_${Date.now()}_${Math.random()}`,
        action: order.side as 'buy' | 'sell' || 'buy',
        symbol: order.symbol || 'UNKNOWN',
        quantity: safeParseFloat(order.qty || order.quantity, 1),
          price: orderPrice,
        time: new Date(executionTime).toLocaleString(),
        status: order.status as TradeActivity['status'] || 'pending',
        timeAgo: getTimeAgo(executionTime),
          source: tradingMode.toLowerCase() as 'live' | 'paper', // Use actual trading mode
        strategyId: order.strategy_id,
          strategyName: order.strategy_name || `${tradingMode} Trading`
        };
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`[TradingHistory] Mapped ${tradingMode} trade using ${timestampSource}:`, trade);
        }
        
        // Add metadata for debugging (won't be displayed but available in console)
        (trade as any)._debug = {
          timestampSource,
          originalOrder: {
            filled_at: order.filled_at,
            submitted_at: order.submitted_at,
            updated_at: order.updated_at,
            created_at: order.created_at,
            status: order.status
          }
        };
        
        return trade;
      })
      .filter(trade => {
        // Filter out orders with invalid or zero prices (unless they're pending orders)
        const hasValidPrice = trade.price > 0 || trade.status === 'pending';
        if (!hasValidPrice) {
          console.warn(`[TradingHistory] FILTERING OUT TRADE - Invalid price:`, {
            symbol: trade.symbol,
            price: trade.price,
            status: trade.status,
            originalOrderData: (trade as any)._debug?.originalOrder
          });
        }
        return hasValidPrice;
      });

      // If no orders but we have positions, create TradeActivity objects from positions (same logic as TradingActivity.tsx)
      let finalTrades = mappedTrades;
      
      console.log(`[TradingHistory] DATA ANALYSIS:`, {
        paperOrders: paperOrders.length,
        liveOrders: liveOrders.length,
        totalVmOrders: vmOrders.length,
        mappedTrades: mappedTrades.length,
        vmPositions: vmPositions.length,
        willFallbackToPositions: mappedTrades.length === 0 && vmPositions.length > 0
      });
      
      if (mappedTrades.length === 0 && vmPositions.length > 0) {
        console.warn(`[TradingHistory] FALLBACK TO POSITIONS: No orders found (${paperOrders.length} paper + ${liveOrders.length} live = 0 valid trades), creating trades from ${vmPositions.length} positions`);
        
        // Log sample order data to debug why orders aren't being processed
        if (paperOrders.length > 0) {
          console.log(`[TradingHistory] Sample paper order that was filtered out:`, paperOrders[0]);
        }
        if (liveOrders.length > 0) {
          console.log(`[TradingHistory] Sample live order that was filtered out:`, liveOrders[0]);
        }
        
        const positionTrades: TradeActivity[] = [];
        
        // Process PAPER positions
        const paperPositions = vmPositions.filter(pos => pos.trading_mode === 'PAPER' || pos._tradingMode === 'PAPER');
        paperPositions.forEach((position, index) => {
          const stableId = `position_${position.asset_id || position.symbol}_PAPER`;
          // Enhanced position timestamp handling - try to get the most accurate time
          let positionTimestamp: string;
          let positionTimestampSource = 'fallback';
          
          // For positions, we want the earliest entry time if available
          if (position.created_at) {
            positionTimestamp = position.created_at;
            positionTimestampSource = 'created_at';
          } else if (position.updated_at) {
            positionTimestamp = position.updated_at;
            positionTimestampSource = 'updated_at';
          } else {
            // Use varied fallback timestamps to spread positions over last few days
            const hoursAgo = 6 + (index * 4); // 6h, 10h, 14h, etc. for different positions
            positionTimestamp = new Date(Date.now() - (hoursAgo * 60 * 60 * 1000)).toISOString();
            positionTimestampSource = 'estimated_spread_paper';
          }
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`[TradingHistory] Position ${position.symbol} timestamp:`, {
              created_at: position.created_at,
              updated_at: position.updated_at,
              chosen: positionTimestamp,
              source: positionTimestampSource
            });
          }
          
                                            positionTrades.push({
              id: stableId,
              action: (parseFloat(position.qty || '0') > 0 ? 'buy' : 'sell') as 'buy' | 'sell',
              symbol: position.symbol || 'UNKNOWN',
              quantity: Math.abs(Math.round(safeParseFloat(position.qty || position.quantity, 1))),
              price: safeParseFloat(position.avg_entry_price || position.current_price, 0),
              time: new Date(positionTimestamp).toLocaleString(),
              status: 'filled' as TradeActivity['status'],
              timeAgo: getTimeAgo(positionTimestamp),
              source: 'paper' as 'paper',
              strategyId: position.strategy_id || 'vm_strategy',
              strategyName: position.strategy_name || 'Paper Trading'
            });
        });
        
        // Process LIVE positions  
        const livePositions = vmPositions.filter(pos => pos.trading_mode === 'LIVE' || pos._tradingMode === 'LIVE' || (!pos.trading_mode && !pos._tradingMode));
        livePositions.forEach((position, index) => {
          const stableId = `position_${position.asset_id || position.symbol}_LIVE`;
          // Enhanced position timestamp handling - try to get the most accurate time
          let positionTimestamp: string;
          let positionTimestampSource = 'fallback';
          
          // For positions, we want the earliest entry time if available
          if (position.created_at) {
            positionTimestamp = position.created_at;
            positionTimestampSource = 'created_at';
          } else if (position.updated_at) {
            positionTimestamp = position.updated_at;
            positionTimestampSource = 'updated_at';
          } else {
            // Use varied fallback timestamps to spread LIVE positions over last few days  
            const hoursAgo = 12 + (index * 6); // 12h, 18h, 24h, etc. for LIVE positions (different from paper)
            positionTimestamp = new Date(Date.now() - (hoursAgo * 60 * 60 * 1000)).toISOString();
            positionTimestampSource = 'estimated_spread_live';
          }
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`[TradingHistory] LIVE Position ${position.symbol} timestamp:`, {
              created_at: position.created_at,
              updated_at: position.updated_at,
              chosen: positionTimestamp,
              source: positionTimestampSource
            });
          }
          
          positionTrades.push({
            id: stableId,
            action: (parseFloat(position.qty || '0') > 0 ? 'buy' : 'sell') as 'buy' | 'sell',
            symbol: position.symbol || 'UNKNOWN',
            quantity: Math.abs(Math.round(safeParseFloat(position.qty || position.quantity, 1))),
            price: safeParseFloat(position.avg_entry_price || position.current_price, 0),
            time: new Date(positionTimestamp).toLocaleString(),
            status: 'filled' as TradeActivity['status'],
            timeAgo: getTimeAgo(positionTimestamp),
            source: 'live' as 'live',
            strategyId: position.strategy_id || 'vm_strategy',
            strategyName: position.strategy_name || 'Live Trading'
          });
        });
        
        finalTrades = positionTrades;
        console.log(`[TradingHistory] Created ${positionTrades.length} trades from positions`);
        console.log(`[TradingHistory] Position trade symbols:`, positionTrades.map(t => `${t.symbol} (${t.source})`));
      }

      // Map VM orders to OrderActivity  
      const mappedOrders: OrderActivity[] = [...paperOrders, ...liveOrders].map((order: any, index: number) => {
        // Determine trading mode based on which array this order came from
        const isLiveOrder = index >= paperOrders.length;
        const tradingMode = isLiveOrder ? 'LIVE' : 'PAPER';
        
        // Map status with case normalization, default to 'open' if status is not supported by OrderActivity
        const rawStatus = (order.status || '').toLowerCase();
        let status: OrderActivity['status'] = 'open'; // default
        if (['open', 'filled', 'cancelled', 'rejected'].includes(rawStatus)) {
          status = rawStatus as OrderActivity['status'];
        }
        
        // Use better timestamp for orders
        const orderTimestamp = order.updated_at || order.created_at || new Date().toISOString();
        
        return {
          id: order.id || `vm_order_${Date.now()}_${Math.random()}`,
          symbol: order.symbol || 'UNKNOWN',
          action: order.side as 'buy' | 'sell' || 'buy',
          type: order.type as 'market' | 'limit' | 'stop' || 'market',
          quantity: safeParseFloat(order.qty || order.quantity, 1),
          price: order.filled_avg_price ? safeParseFloat(order.filled_avg_price) : undefined,
          status: status,
          timeAgo: getTimeAgo(orderTimestamp),
          source: tradingMode.toLowerCase() as 'live' | 'paper'
        };
      });

      // Create strategy activities from deployed strategies instead of hardcoded data
      const realStrategies: StrategyActivity[] = [];
      
      if (deployedStrategies.success && Array.isArray(deployedStrategies.deployed_strategies)) {
        console.log(`[TradingHistory] Processing ${deployedStrategies.deployed_strategies.length} deployed strategies`);
        
        deployedStrategies.deployed_strategies.forEach((strategy: any) => {
          // Calculate performance from positions associated with this strategy
          const strategyPositions = vmPositions.filter(pos => 
            pos.strategy_id === strategy.strategy_id || 
            pos.strategy_name === strategy.name
          );
          
          const totalPnL = strategyPositions.reduce((sum, pos) => 
            sum + safeParseFloat(pos.unrealized_pl || pos.unrealized_pnl, 0), 0
          );
          
          const totalCostBasis = strategyPositions.reduce((sum, pos) => 
            sum + safeParseFloat(pos.cost_basis, 0), 0
          );
          
          const performancePercentage = totalCostBasis > 0 ? (totalPnL / totalCostBasis) * 100 : 0;
          
          realStrategies.push({
            id: strategy.strategy_id,
            name: strategy.name || 'Unknown Strategy',
            type: strategy.trading_mode?.toLowerCase() === 'live' ? 'live' : 'paper',
            lastUpdate: strategy.deployment_time || new Date().toISOString(),
            timeAgo: strategy.status === 'active' ? 'Active' : getTimeAgo(strategy.deployment_time || new Date().toISOString()),
            performance: {
              value: totalPnL,
              percentage: performancePercentage
            },
            positions: strategyPositions.length,
            tradingMode: strategy.trading_mode,
            status: strategy.status,
            symbols: strategy.symbols || []
          });
        });
      }
      
      // Fallback: If no deployed strategies but we have positions, create a summary strategy
      if (realStrategies.length === 0 && vmPositions.length > 0) {
        console.log(`[TradingHistory] No deployed strategies found, creating summary from ${vmPositions.length} positions`);
        
        const totalPnL = vmPositions.reduce((sum, pos) => sum + safeParseFloat(pos.unrealized_pl || pos.unrealized_pnl, 0), 0);
        const totalCostBasis = vmPositions.reduce((sum, pos) => sum + safeParseFloat(pos.cost_basis, 0), 0);
        const performancePercentage = totalCostBasis > 0 ? (totalPnL / totalCostBasis) * 100 : 0;
        
        // Group positions by trading mode
        const livePositionsCount = vmPositions.filter(pos => 
          pos.trading_mode === 'LIVE' || pos._tradingMode === 'LIVE' || (!pos.trading_mode && !pos._tradingMode)
        ).length;
        
        const paperPositionsCount = vmPositions.filter(pos => 
          pos.trading_mode === 'PAPER' || pos._tradingMode === 'PAPER'
        ).length;
        
        if (livePositionsCount > 0) {
          realStrategies.push({
            id: 'vm-live-summary',
            name: 'Live Trading Positions',
            type: 'live',
            lastUpdate: new Date().toISOString(),
            timeAgo: 'Active',
            performance: {
              value: totalPnL,
              percentage: performancePercentage
            },
            positions: livePositionsCount,
            tradingMode: 'LIVE',
            status: 'active'
          });
        }
        
        if (paperPositionsCount > 0) {
          realStrategies.push({
            id: 'vm-paper-summary',
            name: 'Paper Trading Positions',
            type: 'paper',
            lastUpdate: new Date().toISOString(),
            timeAgo: 'Active',
            performance: {
              value: 0, // Paper trading doesn't have real PnL
              percentage: 0
            },
            positions: paperPositionsCount,
            tradingMode: 'PAPER',
            status: 'active'
          });
        }
      }

      if (process.env.NODE_ENV === 'development') {
        console.log(`[TradingHistory] VM data loaded: ${finalTrades.length} trades, ${mappedOrders.length} orders, ${vmPositions.length} positions, ${realStrategies.length} strategies`);
        console.log(`[TradingHistory] PAPER orders: ${paperOrders.length}, LIVE orders: ${liveOrders.length}`);
        console.log(`[TradingHistory] Mapped trades:`, finalTrades);
        console.log(`[TradingHistory] Mapped orders:`, mappedOrders);
        console.log(`[TradingHistory] Real strategies:`, realStrategies);
        
        // Timestamp debugging summary
        const timestampSources = finalTrades.map(t => (t as any)._debug?.timestampSource).filter(Boolean);
        const timestampCounts = timestampSources.reduce((acc, source) => {
          acc[source] = (acc[source] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);
        console.log(`[TradingHistory] Timestamp sources used:`, timestampCounts);
        
        // Add debug utility to global scope for testing
        (window as any).debugTradingHistory = {
          paperOrders,
          liveOrders,
          vmPositions,
          finalTrades,
          timestampCounts,
          testTimestamp: (dateString: string) => {
            const result = {
              input: dateString,
              parsed: new Date(dateString),
              timeAgo: getTimeAgo(dateString),
              isValid: !isNaN(new Date(dateString).getTime())
            };
            console.log('Timestamp test result:', result);
            return result;
          }
        };
      }
      
      setTradeActivities(finalTrades);
      setOrderActivities(mappedOrders);
      setStrategyActivities(realStrategies);
      setConnectionStatus('connected');
      setLastRefreshTime(new Date());
      
      // Success notification for manual refresh
      if (isManualRefresh) {
        toast({
          title: "Data refreshed",
          description: `Updated ${finalTrades.length} trades, ${realStrategies.length} strategies`,
        });
      }
      
    } catch (error: any) {
      console.error('[TradingHistory] Error fetching VM data:', error);
      setConnectionStatus('disconnected');
      
      // Determine error type for better user messaging
      let errorTitle = "Unable to fetch VM data";
      let errorDescription = "Failed to connect to VM trading service.";
      
      if (error.message === 'Request timeout') {
        errorTitle = "Connection timeout";
        errorDescription = "VM trading service is taking too long to respond. Please try again.";
      } else if (error.message.includes('fetch')) {
        errorTitle = "Network error";
        errorDescription = "Unable to reach VM trading service. Check your network connection.";
      }
      
      setErrorMessage(errorDescription);
      
      // Show user-friendly error message
      toast({
        title: errorTitle,
        description: errorDescription,
        variant: "destructive",
      });
      
      // Only clear data on initial load, not on refresh attempts
      if (!isManualRefresh) {
        setTradeActivities([]);
        setOrderActivities([]);
        setStrategyActivities([]);
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchBrokerData();
  }, []);
  
  // Auto-refresh data every 60 seconds to keep it current (increased from 30s to reduce load)
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      console.log('[TradingHistory] Auto-refreshing data...');
      fetchBrokerData(false); // Silent auto-refresh
    }, 60000); // 60 seconds
    
    return () => clearInterval(refreshInterval);
  }, []);
  
  // Manual refresh function
  const handleManualRefresh = () => {
    fetchBrokerData(true);
  };
  
  // Filter helpers for different data sources
  const liveActivities = tradeActivities.filter(activity => activity.source === 'live');
  const paperActivities = tradeActivities.filter(activity => activity.source === 'paper');
  const backtestActivities = tradeActivities.filter(activity => activity.source === 'backtest');
  
  // Helper to format currency
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  // Render an empty state for when we have no data
  const renderEmptyState = (type: string) => (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <FolderPlus className="w-12 h-12 text-gray-400 mb-4" />
      <h3 className="font-medium text-gray-900 mb-2">No {type} History</h3>
      <p className="text-sm text-gray-500 mb-4 max-w-md">
        {type === 'Activity' 
          ? 'Create a trading strategy to start seeing your activity here'
          : type === 'Strategy' 
            ? 'Run a trading strategy to see performance metrics here'
            : 'Place an order to see your order history here'
        }
      </p>
      <Button 
        variant="outline"
        onClick={() => navigate('/dashboard')}
        className="mt-2"
      >
        Go to Dashboard
      </Button>
    </div>
  );

  // Render a trade activity card
  const renderTradeActivity = (activity: TradeActivity) => {
    // Get icon and colors based on trade action and status
    let Icon = activity.action === 'buy' ? TrendingUp : TrendingDown;
    let textColor = activity.action === 'buy' ? 'text-green-600' : 'text-red-600';
    let bgColor = activity.action === 'buy' ? 'bg-green-50' : 'bg-red-50';
    let borderColor = activity.action === 'buy' ? 'border-l-green-500' : 'border-l-red-500';
    
    // Status icon and adjustments
    let StatusIcon = CheckCircle;
    let statusColor = 'text-green-600';
    let statusBg = 'bg-green-50';
    
    switch (activity.status) {
      case 'pending':
        StatusIcon = Clock;
        statusColor = 'text-yellow-600';
        statusBg = 'bg-yellow-50';
        break;
      case 'cancelled':
        StatusIcon = AlertCircle;
        statusColor = 'text-gray-600';
        statusBg = 'bg-gray-50';
        break;
      case 'failed':
        StatusIcon = AlertCircle;
        statusColor = 'text-red-600';
        statusBg = 'bg-red-50';
        break;
    }

    return (
      <div 
        key={activity.id} 
        className={`flex items-center p-3 rounded-md border-l-4 ${borderColor} ${bgColor} mb-3 transition-colors duration-150 hover:bg-opacity-70`}
      >
        <div className="flex-shrink-0 mr-3">
          <Icon className={`w-5 h-5 ${textColor}`} />
        </div>
        
        <div className="flex-grow">
          <div className="flex items-center">
            <span className={`font-medium ${textColor}`}>
              {activity.action === 'buy' ? 'Buy' : 'Sell'}
            </span>
            <span className="mx-1 font-medium">{activity.quantity}</span>
            <span className="font-bold">{activity.symbol}</span>
            <span className="mx-1">@</span>
            <span className="font-medium">{formatCurrency(activity.price)}</span>
          </div>
          
          {activity.strategyName && (
            <div className="text-xs text-gray-500 mt-1">
              <span>Strategy: {activity.strategyName}</span>
            </div>
          )}
          
          <div className="flex items-center text-xs text-gray-500 mt-1">
            <Clock className="w-3 h-3 mr-1" />
            <span>{activity.time}</span>
            
            {/* Timestamp source indicator (only in development) */}
            {process.env.NODE_ENV === 'development' && (activity as any)._debug?.timestampSource && (
              <div className={`ml-2 px-1.5 py-0.5 rounded text-xs font-mono ${
                (activity as any)._debug.timestampSource === 'filled_at' ? 'bg-green-100 text-green-700' :
                (activity as any)._debug.timestampSource === 'submitted_at' ? 'bg-blue-100 text-blue-700' :
                (activity as any)._debug.timestampSource === 'estimated_spread_paper' ? 'bg-orange-100 text-orange-700' :
                (activity as any)._debug.timestampSource === 'estimated_spread_live' ? 'bg-red-100 text-red-700' :
                (activity as any)._debug.timestampSource === 'estimated_recent' ? 'bg-red-100 text-red-700' :
                'bg-yellow-100 text-yellow-700'
              }`}>
                {(activity as any)._debug.timestampSource}
              </div>
            )}
            
            {/* Show if this is a position-based trade (fallback) */}
            {process.env.NODE_ENV === 'development' && activity.id.includes('position_') && (
              <div className="ml-2 px-1.5 py-0.5 rounded text-xs font-mono bg-orange-100 text-orange-700">
                POS-BASED
              </div>
            )}
            
            <div className={`ml-3 px-2 py-0.5 rounded-full ${statusBg} ${statusColor} flex items-center`}>
              <StatusIcon className="w-3 h-3 mr-1" />
              <span className="capitalize">{activity.status}</span>
            </div>
            
            <div className="ml-3 px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 flex items-center">
              <span className="capitalize">{activity.source}</span>
            </div>
          </div>
        </div>
        
        <span className="text-sm text-gray-500 ml-3">{activity.timeAgo}</span>
      </div>
    );
  };
  
  // Calculate execution rate from trade activities
  const calculateExecutionRate = (): string => {
    if (tradeActivities.length === 0) return 'N/A';

    // Count filled trades vs total trades
    const executedTrades = tradeActivities.filter(trade => 
      trade.status === 'filled'
    );

    if (executedTrades.length === 0) return '—';

    // Calculate execution rate (not win rate - we don't have P&L data)
    const executionRate = Math.round((executedTrades.length / tradeActivities.length) * 100);
    return `${executionRate}%`;
  };

  // Summary cards for different types of data
  const renderSummaryCards = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Trades</CardTitle>
          <BarChart2 className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{tradeActivities.length}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Execution Rate</CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{calculateExecutionRate()}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Active Strategies</CardTitle>
          <BarChart2 className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{strategyActivities.length}</div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation 
        activeView={activeView}
        setActiveView={setActiveView}
        showRoles={showRoles}
        setShowRoles={setShowRoles}
      />

      <div className="flex">
        <Sidebar className="hidden md:block" />
        
        <div className="flex-1 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="hover:bg-gray-100"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-2xl font-semibold">Trading History</h1>
            </div>
            
            {/* Connection Status and Refresh */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm">
                {connectionStatus === 'connected' ? (
                  <Wifi className="w-4 h-4 text-green-600" />
                ) : connectionStatus === 'disconnected' ? (
                  <WifiOff className="w-4 h-4 text-red-600" />
                ) : (
                  <div className="w-4 h-4 animate-pulse bg-gray-400 rounded-full" />
                )}
                <span className={`${
                  connectionStatus === 'connected' ? 'text-green-600' : 
                  connectionStatus === 'disconnected' ? 'text-red-600' : 'text-gray-500'
                }`}>
                  {connectionStatus === 'connected' ? 'Connected' : 
                   connectionStatus === 'disconnected' ? 'Disconnected' : 'Connecting...'}
                </span>
              </div>
              
              {lastRefreshTime && (
                <span className="text-xs text-gray-500">
                  Last update: {lastRefreshTime.toLocaleTimeString()}
                </span>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleManualRefresh}
                disabled={isRefreshing}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
          
          {/* Error Banner */}
          {errorMessage && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <span className="text-sm text-red-800">{errorMessage}</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleManualRefresh}
                  className="ml-auto text-red-600 hover:text-red-700"
                >
                  Try Again
                </Button>
              </div>
            </div>
          )}
          
          <Tabs defaultValue="all" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3 max-w-[400px]">
              <TabsTrigger value="all">All Activity</TabsTrigger>
              <TabsTrigger value="strategies">Strategies</TabsTrigger>
              <TabsTrigger value="orders">Orders</TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="space-y-6">
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-pulse text-gray-500">Loading activity...</div>
                </div>
              ) : tradeActivities.length > 0 ? (
                <>
                  {renderSummaryCards()}
                  
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Recent Activity</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {tradeActivities.map(activity => renderTradeActivity(activity))}
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : (
                renderEmptyState('Activity')
              )}
            </TabsContent>

            <TabsContent value="strategies" className="space-y-6">
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-pulse text-gray-500">Loading strategies...</div>
                </div>
              ) : strategyActivities.length > 0 ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Strategy Performance</CardTitle>
                    <p className="text-sm text-gray-500">Real-time data from deployed strategies</p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {strategyActivities.map(strategy => (
                        <div key={strategy.id} className="p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <LineChart className={`w-5 h-5 ${strategy.type === 'live' ? 'text-green-600' : 'text-blue-600'}`} />
                              <div>
                                <h3 className="font-semibold text-gray-900">{strategy.name}</h3>
                                <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    strategy.type === 'live' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {strategy.tradingMode || strategy.type.toUpperCase()}
                                  </span>
                                  {strategy.status && (
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      strategy.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                                    }`}>
                                      {strategy.status}
                                    </span>
                                  )}
                                  <span>•</span>
                                  <span>{strategy.positions} positions</span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-600 mb-1">{strategy.timeAgo}</p>
                              <div className="flex flex-col items-end gap-1">
                                <p className={`text-lg font-semibold ${strategy.performance.percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {strategy.performance.percentage >= 0 ? '+' : ''}{strategy.performance.percentage.toFixed(2)}%
                                </p>
                                <p className={`text-sm ${strategy.performance.value >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatCurrency(strategy.performance.value)}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {strategy.symbols && strategy.symbols.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <p className="text-xs text-gray-500 mb-2">Trading Symbols ({strategy.symbols.length}):</p>
                              <div className="flex flex-wrap gap-1">
                                {strategy.symbols.slice(0, 8).map(symbol => (
                                  <span key={symbol} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-mono">
                                    {symbol}
                                  </span>
                                ))}
                                {strategy.symbols.length > 8 && (
                                  <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded text-xs">
                                    +{strategy.symbols.length - 8} more
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                renderEmptyState('Strategy')
              )}
            </TabsContent>

            <TabsContent value="orders" className="space-y-6">
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-pulse text-gray-500">Loading orders...</div>
                </div>
              ) : orderActivities.length > 0 ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Individual Orders</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {orderActivities.map(order => (
                        <div key={order.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <Clock className="w-4 h-4 text-gray-500" />
                            <div>
                              <p className="font-medium">
                                {order.symbol} {order.action === 'buy' ? 'Buy' : 'Sell'} Order
                              </p>
                              <p className="text-sm text-gray-500">
                                {order.type.charAt(0).toUpperCase() + order.type.slice(1)} @ 
                                {order.price ? ` $${order.price}` : ' Market Price'}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600">{order.timeAgo}</p>
                            <div className={`inline-block px-2 py-1 rounded text-xs font-medium capitalize
                              ${order.status === 'filled' ? 'bg-green-100 text-green-800' : 
                                order.status === 'open' ? 'bg-blue-100 text-blue-800' :
                                order.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                                'bg-red-100 text-red-800'}`}>
                              {order.status}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                renderEmptyState('Order')
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
      
      {/* Bloomberg Radio Button */}
      <BloombergRadioButton />
    </div>
  );
};
